import { createCacheKey, ENDPOINTS, fetchResource, useCreatePayload } from 'hammock';
import { useMemo } from 'react';
import { useQueries } from 'react-query';

import { Entitlement } from '@candu-ai/organizations/entitlements';
import { OrganizationId } from '@candu-ai/organizations/types';
import { Flags, useFlags } from 'commons/hooks';

type EntitlementsQueryResult = {
  id: OrganizationId;
  entitlements: Entitlement[];
};

export const useOrganizationEntitlements = (
  organizationId?: OrganizationId,
): {
  entitlements: Entitlement[];
  entitlementsUsage: Entitlement[];
  isLoading: boolean;
  isError: boolean;
} => {
  const { [Flags.ENTITLEMENTS]: withEntitlements } = useFlags();
  const { get: getEntitlements } = useCreatePayload(ENDPOINTS.entitlements);
  const { get: getEntitlementsUsage } = useCreatePayload(ENDPOINTS.entitlementsUsage);

  const entitlementsPayload = useMemo(() => getEntitlements({ id: organizationId || '' }), [
    organizationId,
  ]);
  const entitlementsUsagePayload = useMemo(
    () => getEntitlementsUsage({ id: organizationId || '' }),
    [organizationId],
  );

  const [entitlementsData, entitlementsUsageData] = useQueries([
    {
      queryKey: createCacheKey(entitlementsPayload),
      queryFn: () => fetchResource<EntitlementsQueryResult>(entitlementsPayload),
      enabled: !!withEntitlements && !!organizationId,
    },
    {
      queryKey: createCacheKey(entitlementsUsagePayload),
      queryFn: () => fetchResource<EntitlementsQueryResult>(entitlementsUsagePayload),
      enabled: !!withEntitlements && !!organizationId,
    },
  ]);

  return {
    entitlements: entitlementsData?.data?.entitlements || [],
    entitlementsUsage: entitlementsUsageData?.data?.entitlements || [],
    isLoading: entitlementsData?.isLoading || entitlementsUsageData?.isLoading,
    isError: entitlementsData?.isError || entitlementsUsageData?.isError || !organizationId,
  };
};
